import React from 'react';
import {Link} from 'react-router';
import type {BlockInstance} from '../appearance-data';
import {EyebrowPill} from './eyebrow-pill';

type P = {block: BlockInstance; props: Record<string, any>};

interface Story {
  label?: string;
  image?: string;
  video?: string;
  text?: string;
  link?: string;
  link_label?: string;
  seconds?: number | string;
}

/**
 * Parse a story's video URL into how to play it plus an auto thumbnail.
 * - YouTube / Vimeo → an embeddable iframe URL (advances on the story timer,
 *   since we can't detect the end of an embed).
 * - Anything else → treated as a direct file (mp4/webm) played in <video>.
 * A YouTube thumbnail is derived automatically when the story has no photo.
 */
function parseVideo(url?: string): {kind: 'youtube' | 'vimeo' | 'file' | 'none'; embed?: string; thumb?: string} {
  const u = (url || '').trim();
  if (!u) return {kind: 'none'};
  const yt = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/))([\w-]{6,})/);
  if (yt) {
    return {
      kind: 'youtube',
      embed: `https://www.youtube.com/embed/${yt[1]}?autoplay=1&mute=1&playsinline=1&rel=0&modestbranding=1`,
      thumb: `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`,
    };
  }
  const vimeo = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return {kind: 'vimeo', embed: `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1&muted=1`};
  return {kind: 'file'};
}

/** Thumbnail for a story bubble/tile: explicit photo, else an auto video thumb. */
const storyThumb = (s: Story): string | undefined => s.image || parseVideo(s.video).thumb;

/**
 * Social-media style "Stories": a row of circular bubbles that open a fullscreen
 * viewer with progress bars, auto-advancing photo/text stories (tap to move,
 * optional CTA link per story).
 */
export function StoriesBlock({props}: P) {
  const [open, setOpen] = React.useState<number | null>(null);
  const defaultSeconds = Math.max(2, Number(props.default_seconds) || 5);
  const grid = props.layout === 'grid';

  const limit = Math.max(0, Number(props.limit) || 0);
  const allStories: Story[] = Array.isArray(props.items)
    ? props.items.filter((s: Story) => s.image || s.text || s.video)
    : [];
  const stories = limit > 0 ? allStories.slice(0, limit) : allStories;

  // Grid "per row" count (desktop). Mobile/tablet stay at 2/3 for readability.
  const gridCols =
    props.columns === '3' ? 'lg:grid-cols-3'
    : props.columns === '4' ? 'lg:grid-cols-4'
    : props.columns === '6' ? 'lg:grid-cols-6'
    : 'lg:grid-cols-5';

  if (!stories.length) return null;

  return (
    <section className="px-24 py-24">
      <div className="mx-auto max-w-[1100px]">
        <EyebrowPill text={props.eyebrow} />
        {props.heading ? <h2 className="mb-16 text-center text-xl font-bold">{props.heading}</h2> : null}
        {grid ? (
          // Grid layout: centered responsive grid of poster tiles.
          <div className={`grid grid-cols-2 justify-items-center gap-16 sm:grid-cols-3 md:grid-cols-4 ${gridCols}`}>
            {stories.map((s, i) => {
              const thumb = storyThumb(s);
              return (
                <button key={i} type="button" onClick={() => setOpen(i)}
                  className="group/story flex w-full max-w-160 flex-col items-center gap-8">
                  <span className="relative w-full overflow-hidden rounded-2xl bg-primary/10 shadow-sm ring-1 ring-divider" style={{aspectRatio: '3 / 4'}}>
                    {thumb ? (
                      <img src={thumb} alt={s.label || ''} className="h-full w-full object-cover transition group-hover/story:scale-105" loading="lazy" />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center text-primary">
                        <span className="material-icons text-4xl">{s.video ? 'play_circle' : 'campaign'}</span>
                      </span>
                    )}
                    {s.video && thumb ? (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="material-icons text-4xl text-white drop-shadow-lg">play_circle</span>
                      </span>
                    ) : null}
                  </span>
                  {s.label ? <span className="line-clamp-2 text-center text-xs font-medium text-main">{s.label}</span> : null}
                </button>
              );
            })}
          </div>
        ) : (
          // Circle layout: centered row of story bubbles (wraps on small screens).
          <div className="flex flex-wrap justify-center gap-16">
            {stories.map((s, i) => {
              const thumb = storyThumb(s);
              return (
                <button key={i} type="button" onClick={() => setOpen(i)}
                  className="group/story relative flex w-72 shrink-0 flex-col items-center gap-6">
                  {/* Ring spins slowly on hover and the bubble lifts, so the row
                      reads as tappable rather than decorative. */}
                  <span className="rounded-full bg-gradient-to-tr from-primary via-fuchsia-500 to-amber-400 p-3 transition-transform duration-300 group-hover/story:-translate-y-1 group-hover/story:scale-105">
                    <span className="relative block rounded-full bg-paper p-2">
                      {thumb ? (
                        <img src={thumb} alt={s.label || ''} className="h-60 w-60 rounded-full object-cover" loading="lazy" />
                      ) : (
                        <span className="flex h-60 w-60 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <span className="material-icons">{s.video ? 'play_circle' : 'campaign'}</span>
                        </span>
                      )}
                      {s.video && thumb ? (
                        <span className="absolute inset-0 flex items-center justify-center">
                          <span className="material-icons text-2xl text-white drop-shadow-lg">play_circle</span>
                        </span>
                      ) : null}
                    </span>
                  </span>
                  {s.label ? <span className="max-w-72 truncate text-xs text-muted">{s.label}</span> : null}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {open != null && (
        <StoryViewer
          stories={stories}
          start={open}
          defaultSeconds={defaultSeconds}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}

function StoryViewer({
  stories,
  start,
  defaultSeconds,
  onClose,
}: {
  stories: Story[];
  start: number;
  defaultSeconds: number;
  onClose: () => void;
}) {
  const [index, setIndex] = React.useState(start);
  const [paused, setPaused] = React.useState(false);
  // A video story's length is used for its progress bar / auto-advance once the
  // metadata loads (falls back to the configured seconds until then).
  const [videoSeconds, setVideoSeconds] = React.useState(0);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const story = stories[index];
  const media = parseVideo(story?.video);
  const seconds = Math.max(2, Number(story?.seconds) || videoSeconds || defaultSeconds);

  const next = React.useCallback(() => {
    setIndex(i => {
      if (i + 1 >= stories.length) { onClose(); return i; }
      return i + 1;
    });
  }, [stories.length, onClose]);
  const prev = () => setIndex(i => Math.max(0, i - 1));

  // Reset the measured video length whenever the story changes.
  React.useEffect(() => setVideoSeconds(0), [index]);

  // Auto-advance timer (restarts on index change; pauses while held).
  React.useEffect(() => {
    if (paused) return;
    const t = setTimeout(next, seconds * 1000);
    return () => clearTimeout(t);
  }, [index, paused, seconds, next]);

  // Keep the <video> in sync with the hold-to-pause gesture.
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused) v.pause();
    else v.play().catch(() => {});
  }, [paused, index]);

  // Keyboard: arrows + escape.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, onClose]);

  if (!story) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90" role="dialog" aria-modal="true">
      <div className="relative flex h-full w-full max-w-460 flex-col overflow-hidden bg-black">
        {/* Progress bars */}
        <div className="absolute inset-x-0 top-0 z-20 flex gap-4 p-8">
          {stories.map((_, i) => (
            <span key={i} className="h-3 flex-1 overflow-hidden rounded-full bg-white/30">
              <span
                className="block h-full bg-white"
                style={{
                  width: i < index ? '100%' : i === index ? undefined : '0%',
                  animation: i === index && !paused ? `mtk-story-fill ${seconds}s linear forwards` : undefined,
                }}
              />
            </span>
          ))}
        </div>

        {/* Close */}
        <button onClick={onClose} className="absolute right-10 top-18 z-20 text-white/90 hover:text-white">
          <span className="material-icons">close</span>
        </button>

        {/* Media + caption */}
        <div
          className="relative flex flex-1 select-none items-center justify-center"
          onMouseDown={() => setPaused(true)}
          onMouseUp={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
          onTouchEnd={() => setPaused(false)}
        >
          {media.kind === 'youtube' || media.kind === 'vimeo' ? (
            // Embedded video (YouTube/Vimeo): autoplay muted. We can't detect its
            // end, so it advances on the story timer (set "Seconds" to its length).
            <iframe
              src={media.embed}
              className="absolute inset-0 m-auto aspect-[9/16] h-full w-full max-w-full"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              title={story.label || 'video'}
            />
          ) : media.kind === 'file' ? (
            // Direct video file: autoplay (muted so browsers allow it), advance when
            // it ends; its duration also drives the progress bar via videoSeconds.
            <video
              ref={videoRef}
              src={story.video}
              poster={story.image || undefined}
              className="absolute inset-0 m-auto h-full w-full object-contain"
              autoPlay
              muted
              playsInline
              onEnded={next}
              onLoadedMetadata={e => {
                const d = e.currentTarget.duration;
                if (Number.isFinite(d) && d > 0) setVideoSeconds(Math.ceil(d));
              }}
            />
          ) : story.image ? (
            // contain = the WHOLE story image is visible, scaled to full height
            // (portrait posters show completely; no cropping).
            <img src={story.image} alt="" className="absolute inset-0 m-auto h-full w-full object-contain" />
          ) : null}
          {(story.text || story.link) && (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-20 pt-40 text-white">
              {story.text && <p className="mb-12 whitespace-pre-line text-sm leading-relaxed">{story.text}</p>}
              {story.link && (
                story.link.startsWith('http') ? (
                  <a href={story.link} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-4 rounded-full bg-white px-16 py-8 text-sm font-semibold text-black">
                    {story.link_label || 'Learn more'}
                    <span className="material-icons text-base">arrow_forward</span>
                  </a>
                ) : (
                  <Link to={story.link} onClick={onClose}
                    className="inline-flex items-center gap-4 rounded-full bg-white px-16 py-8 text-sm font-semibold text-black">
                    {story.link_label || 'Learn more'}
                    <span className="material-icons text-base">arrow_forward</span>
                  </Link>
                )
              )}
            </div>
          )}

          {/* Tap zones: left = prev, right = next */}
          <button aria-label="Previous" onClick={prev} className="absolute inset-y-0 left-0 w-1/3" />
          <button aria-label="Next" onClick={next} className="absolute inset-y-0 right-0 w-1/3" />
        </div>
      </div>
      <style>{`@keyframes mtk-story-fill{from{width:0%}to{width:100%}}`}</style>
    </div>
  );
}
